import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useMenu } from '../../context/MenuContext';
import Loading from './Loading';

const ProtectedRoute = ({
    children,
    requiredRole = null,
    adminOnly = false,
    xdAllowed = false,
    lectorAllowed = false,
    menuKey = null,
}) => {
    const { isAuthenticated, loading, isAdmin, isXD, isLector, hasRole } = useAuth();
    const { canAccess, canAccessPath, firstPath, loading: menuLoading } = useMenu();
    const location = useLocation();

    if (loading) {
        return <Loading message="Verificando autenticación..." fullScreen={true} />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Los usuarios XD acceden a rutas marcadas como xdAllowed, a rutas con
    // menuKey (se resuelven más abajo vía canAccess) o a cualquier otra ruta
    // que el backend le haya otorgado dinámicamente en /me/menu (ej. cuando se
    // le da acceso a "dashboard" u otros ítems sin menuKey propio). Antes esta
    // regla era fija y solo dejaba pasar /my-session y /activos, ignorando el
    // menú dinámico — por eso un usuario XD con más permisos en el backend
    // seguía sin poder navegar ni ver la barra lateral (my-session no usa Layout).
    if (isXD() && !xdAllowed && !menuKey) {
        if (menuLoading) {
            return <Loading message="Verificando permisos..." fullScreen={true} />;
        }
        if (!canAccessPath(location.pathname)) {
            return <Navigate to="/my-session" replace />;
        }
    }

    // Los usuarios lector solo pueden acceder a rutas marcadas como lectorAllowed
    if (isLector() && !lectorAllowed && !menuKey) {
        return <Navigate to="/activos" replace />;
    }

    // Verificación por menu_key (permisos dinámicos desde BD)
    if (menuKey) {
        if (menuLoading) {
            return <Loading message="Verificando permisos..." fullScreen={true} />;
        }
        if (!isAdmin() && !canAccess(menuKey)) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-red-600 mb-4">Acceso Denegado</h1>
                        <p className="text-gray-600 mb-4">Tu rol no tiene acceso a esta sección.</p>
                        <Navigate to={firstPath} replace />
                    </div>
                </div>
            );
        }
        return children;
    }

    // Esta altura del componente solo se alcanza en rutas "genéricas" (sin
    // menuKey propio) — en la práctica, la única es /dashboard. Si el rol del
    // usuario no tiene ese ítem en su menú (SST, RRHH, o cualquier rol nuevo
    // cuyo menú no incluya "dashboard"), lo mandamos a su propia primera
    // página en vez de mostrarle el dashboard genérico con widgets que no le
    // sirven (ej. "Mis Solicitudes" a un usuario de RRHH).
    if (!isAdmin() && !canAccess('dashboard')) {
        if (menuLoading) {
            return <Loading message="Cargando menú..." fullScreen={true} />;
        }
        return <Navigate to={firstPath} replace />;
    }

    // Verificar si se requiere rol de admin
    if (adminOnly && !isAdmin()) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Acceso Denegado</h1>
                    <p className="text-gray-600 mb-4">No tienes permisos de administrador para acceder a esta página.</p>
                    <Navigate to="/dashboard" replace />
                </div>
            </div>
        );
    }

    // Verificar rol específico
    if (requiredRole && !hasRole(requiredRole)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Acceso Denegado</h1>
                    <p className="text-gray-600 mb-4">No tienes el rol necesario para acceder a esta página.</p>
                    <Navigate to="/dashboard" replace />
                </div>
            </div>
        );
    }

    return children;
};

export default ProtectedRoute;
